// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link } from '@tanstack/react-router';

// material ui icons
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';

// material ui
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';

type AnalysisType =
  | 'energy-analysis'
  | 'child-analysis'
  | 'hour-analysis'
  | 'duration-analysis'
  | 'comparison-analysis'
  | 'load-profile-analysis';

interface AnalysisOption {
  value: AnalysisType;
  translationKey: string;
  icon: React.ReactNode;
}

interface AnalysisSelectorProps {
  energyTreeNodeId: string;
  searchParams: URLSearchParams;
}

const AnalysisSelector = ({
  energyTreeNodeId,
  searchParams,
}: AnalysisSelectorProps) => {
  const { t } = useTranslation('energyxplorer-tree-tab');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const analysisOptions: AnalysisOption[] = [
    {
      value: 'energy-analysis',
      translationKey: 'analysis_types.energy_analysis',
      icon: <QueryStatsRoundedIcon sx={{ mr: 1 }} />,
    },
    {
      value: 'child-analysis',
      translationKey: 'analysis_types.child_analysis',
      icon: <BarChartRoundedIcon sx={{ mr: 1 }} />,
    },
    {
      value: 'hour-analysis',
      translationKey: 'analysis_types.hour_analysis',
      icon: <ScheduleRoundedIcon sx={{ mr: 1 }} />,
    },
    {
      value: 'duration-analysis',
      translationKey: 'analysis_types.duration_analysis',
      icon: <TimelineRoundedIcon sx={{ mr: 1 }} />,
    },
    {
      value: 'comparison-analysis',
      translationKey: 'analysis_types.comparison_analysis',
      icon: <CompareArrowsRoundedIcon sx={{ mr: 1 }} />,
    },
    {
      value: 'load-profile-analysis',
      translationKey: 'analysis_types.load_profile_analysis',
      icon: <ShowChartRoundedIcon sx={{ mr: 1 }} />,
    },
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        size="small"
        variant="contained"
        startIcon={<QueryStatsRoundedIcon />}
        endIcon={<ArrowDropDownIcon />}
        aria-controls={menuOpen ? 'analysis-menu' : undefined}
        aria-expanded={menuOpen ? 'true' : undefined}
        aria-haspopup="menu"
        onClick={handleMenuOpen}
      >
        {t('node_dialog.info_node_tab.actions.open_analysis')}
      </Button>
      <Menu
        id="analysis-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        {analysisOptions.map((option) => {
          const newParams = Object.fromEntries(searchParams.entries());
          newParams.energyTreeNodeId = energyTreeNodeId;

          return (
            <MenuItem
              key={option.value}
              component={Link}
              to={`../${option.value}`}
              search={newParams}
              onClick={handleMenuClose}
              sx={{
                ':hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {option.icon}
                <Typography variant="body2" fontWeight="medium">
                  {t(option.translationKey)}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default AnalysisSelector;
